import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom-v5-compat';

import { useCurrentCustomer, useCustomFeatureLimitsData } from 'src/store';
import { useBusinessBookDemo, useIsApplicationModeInfinity } from 'src/services';
import { formatDDTeamsCheckoutLink } from 'src/lib/routing';
import {
  MAX_SEAT_TIER,
  getCustomerPlan,
  getCustomerTier,
  isDDTeamsPlan,
  isEnterpriseOrAcePlan,
  SUBSCRIPTION_PLAN,
} from 'src/data/subscription';
import * as events from 'src/lib/events';
import { BOOK_DEMO_ORIGIN } from 'src/data/common';
import { BusinessBookDemoButton } from 'src/components/BusinessBookDemoButton';
import { PopupHeader } from 'src/components/PopupHeader';
import { LanternIcon } from 'src/assets/icons/Lantern';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { preparePlatformUrl } from 'src/lib/url';
import { useStyles } from './useStyles';

export const UpgradePopup = ({ open, feature, onClose, isPerformanceChannel }) => {
  const styles = useStyles();

  const currentCustomer = useCurrentCustomer();
  const isInfinityAppMode = useIsApplicationModeInfinity();

  const plan = getCustomerPlan(currentCustomer);
  const tier = getCustomerTier(currentCustomer);
  const isDataDriven = isDDTeamsPlan(plan, tier);
  const isTopDDTier = tier === MAX_SEAT_TIER;
  const isBusinessOrEnterprise = isEnterpriseOrAcePlan(plan, tier);
  const isWorkspaceLimit = feature === FEATURE_LIMITS.workspacesPerCustomer;

  const { limit, label, currentCount } = useCustomFeatureLimitsData(feature);
  const { userAllowedToBookDemo, handleBusinessBookDemoClick } = useBusinessBookDemo(
    BOOK_DEMO_ORIGIN.cipPageChannel
  );

  const shouldBookDemo =
    isTopDDTier || (isDataDriven && !isWorkspaceLimit) || isBusinessOrEnterprise;
  const upgradeButtonProps = isInfinityAppMode
    ? {
        component: 'a',
        href: preparePlatformUrl(formatDDTeamsCheckoutLink()),
        target: '_blank',
      }
    : {
        component: Link,
        to: formatDDTeamsCheckoutLink(),
      };

  const handleUpgradeClicked = () => {
    const origin = label + '_limit';
    const plan = isDataDriven
      ? events.convertSubscriptionPlanForHubspot(SUBSCRIPTION_PLAN.business)
      : events.convertSubscriptionPlanForHubspot(SUBSCRIPTION_PLAN.dataDriven);
    events.trackHubspotEvent(events.HUBSPOT_EVENT.clickedUpgradeButton, {
      cta: events.HUBSPOT_SUBSCRIPTION_ACTION.buyNow,
      origin,
      plan,
    });
    onClose();
  };

  return (
    <Dialog
      classes={{ paper: styles.paper }}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="UpgradePopup"
    >
      <PopupHeader
        className={styles.header}
        classes={{ buttonClose: styles.headerButtonClose }}
        onClose={onClose}
      />
      <DialogContent className={styles.dialogContent}>
        <LanternIcon className={styles.icon} />
        <Typography className={styles.title}>It’s time to upgrade!</Typography>
        <Typography className={styles.subtitle}>
          You're using {currentCount}/{limit} {label}
          <br />
        </Typography>
        <Typography className={styles.description}>
          {!isPerformanceChannel && `To add additional ${label}, `}
          {isPerformanceChannel
            ? 'To connect more channels and unlock premium business features, book a demo today!'
            : shouldBookDemo
            ? 'and unlock premium business features book a demo today.'
            : 'upgrade your plan and take your copywriting to the next level.'}
        </Typography>

        {shouldBookDemo ? (
          <BusinessBookDemoButton
            className={styles.button}
            classes={{ button: 'w-full' }}
            onClick={handleBusinessBookDemoClick}
            origin={origin}
            canBook={userAllowedToBookDemo}
            hideLabel
          />
        ) : (
          <Button
            className={styles.button}
            {...upgradeButtonProps}
            onClick={handleUpgradeClicked}
            variant="contained"
            color="secondary"
            data-color-cta
          >
            Upgrade to Data-Driven Teams
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

UpgradePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  channel: PropTypes.string.isRequired,
};

UpgradePopup.defaultProps = {};
